import { useState } from "react";
import { useMutation, UseMutationResult } from "react-query";
import {
  getFleetCapacityReportEntities,
  getFleetCapacityReportTimeframe,
  getFleetCapacityReport
} from "../../../../utils/axios-requests";
import {
  FleetCapacityReportDto,
  FleetCapacityReportEntities,
  FleetCapacityReportTimeframe,
  FleetCapacityReportResponse,
  VehicleCapacityReportItem
} from "../../../../features/Reports/typings/fleet-capacity-report.types";

export type FleetEntitiesParams = {
  start: string;
  end: string;
  timezone: string;
  vehicleIds: number[];
};

/**
 * Intersection of selected date range and API timeframe (ISO date strings).
 * Returns the overlapping range: max of starts, min of ends.
 */
function intersectDateRange(
  selectedStart: string,
  selectedEnd: string,
  timeframe: FleetCapacityReportTimeframe
): { start: string; end: string } {
  const start = selectedStart > timeframe.startDate ? selectedStart : timeframe.startDate;
  const end = selectedEnd < timeframe.endDate ? selectedEnd : timeframe.endDate;
  return { start, end };
}

export type FleetCapacityReportMutationsResult = {
  entitiesData: FleetCapacityReportEntities | null;
  setEntitiesData: (data: FleetCapacityReportEntities | null) => void;
  timeframeData: FleetCapacityReportTimeframe | null;
  capacityData: FleetCapacityReportResponse | null;
  setCapacityData: (data: FleetCapacityReportResponse | null) => void;
  entitiesMutation: UseMutationResult<FleetCapacityReportEntities, unknown, FleetEntitiesParams>;
  timeframeMutation: UseMutationResult<FleetCapacityReportTimeframe, unknown, number[]>;
  capacityMutation: UseMutationResult<FleetCapacityReportResponse, unknown, FleetCapacityReportDto>;
  isFleetLoading: boolean;
  isCapacityLoading: boolean;
  isReportFetching: boolean;
  fetchFleetCapacityReport: (
    params: FleetEntitiesParams & FleetCapacityReportDto
  ) => Promise<FleetCapacityReportResponse | null>;
  /** Fetches only capacity (keeps entities/timeframe). Use when only filter or vehicle selection changed. */
  fetchCapacityOnly: (
    params: FleetCapacityReportDto
  ) => Promise<FleetCapacityReportResponse | null>;
  resetReport: () => void;
};

export function useFleetCapacityReportMutations(): FleetCapacityReportMutationsResult {
  const [entitiesData, setEntitiesData] = useState<FleetCapacityReportEntities | null>(null);
  const [timeframeData, setTimeframeData] = useState<FleetCapacityReportTimeframe | null>(null);
  const [capacityData, setCapacityData] = useState<FleetCapacityReportResponse | null>(null);
  const [isReportFetching, setIsReportFetching] = useState(false);

  const entitiesMutation = useMutation(
    (params: FleetEntitiesParams) =>
      getFleetCapacityReportEntities({
        ...params,
        vehicleIds: params.vehicleIds ?? []
      }).then(res => res.data),
    {
      onSuccess: data => setEntitiesData(data)
    }
  );

  const timeframeMutation = useMutation(
    (vehicleIds: number[]) =>
      getFleetCapacityReportTimeframe({ data: vehicleIds }).then(res => res.data),
    {
      onSuccess: data => setTimeframeData(data)
    }
  );

  const capacityMutation = useMutation(
    (params: FleetCapacityReportDto) => getFleetCapacityReport(params).then(res => res.data),
    {
      onSuccess: data => setCapacityData(data)
    }
  );

  const fetchFleetCapacityReport = async (
    params: FleetEntitiesParams & FleetCapacityReportDto
  ): Promise<FleetCapacityReportResponse | null> => {
    setIsReportFetching(true);
    try {
      const entitiesParams: FleetEntitiesParams = {
        start: params.start,
        end: params.end,
        timezone: params.timezone ?? "UTC",
        vehicleIds: params.vehicleIds ?? []
      };
      const entitiesResult = await entitiesMutation.mutateAsync(entitiesParams);
      const vehicleIds = entitiesResult.vehicles
        .filter(
          (v): v is VehicleCapacityReportItem & { vehicleInventoryId: number } =>
            v.vehicleInventoryId !== undefined
        )
        .map(v => v.vehicleInventoryId);

      if (vehicleIds.length === 0) {
        setTimeframeData(null);
        setCapacityData(null);
        return null;
      }

      const timeframeResult = await timeframeMutation.mutateAsync(vehicleIds);
      const { start: capacityStart, end: capacityEnd } = intersectDateRange(
        params.start,
        params.end,
        timeframeResult
      );

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { vehicleIds: _ignoredVehicleIds, ...restParams } = params;

      const capacityParams: FleetCapacityReportDto = {
        ...restParams,
        start: capacityStart,
        end: capacityEnd,
        filter: vehicleIds
      };

      const capacityResult = await capacityMutation.mutateAsync(capacityParams);
      return capacityResult;
    } finally {
      setIsReportFetching(false);
    }
  };

  const fetchCapacityOnly = async (
    params: FleetCapacityReportDto
  ): Promise<FleetCapacityReportResponse | null> => {
    const capacityParams: FleetCapacityReportDto = timeframeData
      ? {
          ...params,
          ...intersectDateRange(params.start, params.end, timeframeData)
        }
      : params;
    setIsReportFetching(true);
    try {
      return await capacityMutation.mutateAsync(capacityParams);
    } finally {
      setIsReportFetching(false);
    }
  };

  const resetReport = () => {
    setEntitiesData(null);
    setCapacityData(null);
    setTimeframeData(null);
    setIsReportFetching(false);
  };

  return {
    entitiesData,
    setEntitiesData,
    timeframeData,
    capacityData,
    setCapacityData,
    entitiesMutation,
    timeframeMutation,
    capacityMutation,
    fetchFleetCapacityReport,
    fetchCapacityOnly,
    resetReport,
    isFleetLoading: entitiesMutation.isLoading,
    isCapacityLoading: capacityMutation.isLoading,
    isReportFetching
  };
}
